import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAddresses, getToken, clearCart } from '../api/auth';
import { useCurrency } from '../context/CurrencyContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Zone detection ────────────────────────────────────────────
const KERALA_STATES = ['kerala'];
const SOUTH_STATES  = ['tamil nadu', 'karnataka', 'andhra pradesh', 'telangana', 'goa', 'puducherry', 'pondicherry'];

const getZone = (stateName = '') => {
  const s = stateName.toLowerCase().trim();
  if (KERALA_STATES.includes(s)) return 'kerala';
  if (SOUTH_STATES.includes(s))  return 'south';
  return 'north';
};

// ── Delivery options by zone and subtotal ─────────────────────
const getDeliveryOptions = (zone, subtotal) => {
  const freeBase = subtotal >= 1499;
  return [
    {
      id:      'indiapost-standard',
      partner: 'India Post',
      type:    'Standard',
      days:    zone === 'kerala' ? '2–7 days' : '5–10 days',
      charge:  freeBase ? 0 : (zone === 'kerala' ? 70 : 100),
      badge:   'Recommended',
    },
    {
      id:      'dtdc-standard',
      partner: 'DTDC',
      type:    'Standard',
      days:    zone === 'kerala' ? '3–5 days' : '3–7 days',
      charge:  freeBase ? 0 : (zone === 'kerala' || zone === 'south' ? 100 : 150),
      badge:   null,
    },
    {
      id:      'dtdc-express',
      partner: 'DTDC',
      type:    'Express',
      days:    zone === 'kerala' ? '1–2 days' : zone === 'south' ? '2–3 days' : '2–4 days',
      charge:  freeBase
        ? (zone === 'kerala' ? 150 : zone === 'south' ? 250 : 350)
        : (zone === 'kerala' ? 250 : zone === 'south' ? 350 : 450),
      badge:   'Fastest',
    },
  ];
};

// ── Gift voucher slab ─────────────────────────────────────────
const getVoucher = (subtotal) => {
  if (subtotal >= 9999) return { discount: 500, freeGift: true,  label: '🎁 Free Gift + ₹500 off unlocked!' };
  if (subtotal >= 6999) return { discount: 300, freeGift: true,  label: '🎁 Free Gift + ₹300 off unlocked!' };
  if (subtotal >= 3999) return { discount: 150, freeGift: true,  label: '🎁 Free Gift + ₹150 off unlocked!' };
  if (subtotal >= 2999) return { discount: 0,   freeGift: true,  label: '🎁 Free Gift unlocked!' };
  if (subtotal >= 1499) return { discount: 0,   freeGift: false, label: '🚚 Free base shipping unlocked!' };
  const needed = 1499 - subtotal;
  return { discount: 0, freeGift: false, label: `Add ₹${Math.ceil(needed)} more for free base shipping` };
};

// ── International detection ───────────────────────────────────
const WHATSAPP_NUMBER = '919876543210'; // ← replace with your number

const isInternational = (addr) => {
  if (!addr) return false;
  const country = (addr.country || '').toLowerCase().trim();
  return country !== '' && country !== 'india' && country !== 'in';
};

const buildWhatsAppMessage = (items, addr) => {
  const itemLines = items.map(i => `• ${i.name} x${i.qty} — ₹${i.price * i.qty}`).join('\n');
  const addrLine = addr
    ? `${addr.full_name}, ${addr.line_1}${addr.line_2 ? ', ' + addr.line_2 : ''}, ${addr.city}, ${addr.state}, ${addr.country || ''} — ${addr.pincode}`
    : 'No address selected';
  return encodeURIComponent(
    `Hello Aamira Jewellery! I'd like to place an international order.\n\n*Items:*\n${itemLines}\n\n*Subtotal:* ₹${items.reduce((s, i) => s + i.price * i.qty, 0)}\n\n*Delivery address:*\n${addrLine}\n\nPlease share the shipping quote and payment details.`
  );
};

const Checkout = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const items = state?.items || [];
  const { formatPrice } = useCurrency();

  const [addresses,        setAddresses]        = useState([]);
  const [addressId,        setAddressId]        = useState('');
  const [zone,             setZone]             = useState('north');
  const [deliveryOptions,  setDeliveryOptions]  = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [loading,          setLoading]          = useState(false);
  const [err,              setErr]              = useState('');
  const [isIntl,           setIsIntl]           = useState(false);

  // Load addresses on mount
  useEffect(() => {
    if (!getToken()) { navigate('/account'); return; }
    if (items.length === 0) { navigate('/'); return; }
    getAddresses().then(a => {
      setAddresses(a);
      const def = a.find(x => x.is_default) || a[0];
      if (def) setAddressId(def.id);
    });
  }, []);

  // Auto-detect zone + delivery options when address changes
  useEffect(() => {
    if (!addressId || addresses.length === 0) return;
    const addr = addresses.find(a => a.id === addressId);
    if (!addr) return;
    const intl = isInternational(addr);
    setIsIntl(intl);
    if (!intl) {
      const detectedZone = getZone(addr.state);
      setZone(detectedZone);
      const options = getDeliveryOptions(detectedZone, subtotal);
      setDeliveryOptions(options);
      setSelectedDelivery(options[0]);
    } else {
      setSelectedDelivery(null);   // ← only this line is new
      setDeliveryOptions([]);      // ← only this line is new
    }
  }, [addressId, addresses]);

  const subtotal        = items.reduce((s, i) => s + i.price * i.qty, 0);
  const voucher = isIntl ? { discount: 0, freeGift: false, label: null } : getVoucher(subtotal);
  const voucherDiscount = voucher.discount;
  const shippingCharge = isIntl ? null : (selectedDelivery?.charge ?? 0);
  const total = subtotal - voucherDiscount + (shippingCharge ?? 0);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (document.getElementById('razorpay-script')) { resolve(true); return; }
      const script = document.createElement('script');
      script.id  = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePayment = async () => {
    if (!addressId)        { setErr('Please select a delivery address'); return; }
    if (!selectedDelivery) { setErr('Please select a delivery option');  return; }
    setLoading(true); setErr('');

    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      setErr('Failed to load payment gateway. Check your internet connection.');
      setLoading(false); return;
    }

    let orderData;
    let pendingOrder;
    try {
      const res = await fetch(`${API}/orders/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          items: items.map(i => ({
            product_id: i.id,
            qty:        i.qty,
            color:      i.color || null,
            size:       i.size  || null,
          })),
          address_id:       addressId,
          delivery_partner: selectedDelivery.partner,
          delivery_type:    selectedDelivery.type,
          delivery_zone:    zone,
          delivery_charge:  selectedDelivery.charge,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      orderData    = data;
      pendingOrder = data.pendingOrder;
    } catch (e) {
      setErr(e.message);
      setLoading(false); return;
    }

    const selectedAddress = addresses.find(a => a.id === addressId);

    const options = {
      key:         orderData.key_id,
      amount:      Math.round(orderData.total * 100),
      currency:    'INR',
      name:        'Aamira Jewellery',
      description: `Order ${orderData.order_number}`,
      order_id:    orderData.razorpayOrderId,
      prefill: {
        name:    selectedAddress?.full_name || '',
        contact: selectedAddress?.phone     || '',
      },
      theme: { color: '#B08D57' },

      handler: async (response) => {
        try {
          const res = await fetch(`${API}/orders/confirm`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${getToken()}`,
            },
            body: JSON.stringify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              pendingOrder,
            }),
          });
          const data = await res.json();
          if (data.success) {
            try { await clearCart(); } catch (_) {}
            navigate('/order-success', { state: { order: data.order } });
          } else {
            setErr(data.error || 'Order confirmation failed');
          }
        } catch {
          setErr('Network error during confirmation. Please contact support.');
        }
        setLoading(false);
      },

      modal: {
        ondismiss: () => {
          setErr('');
          setLoading(false);
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on('payment.failed', (response) => {
      setErr(`Payment failed: ${response.error.description}`);
      setLoading(false);
    });
    rzp.open();
  };

  if (items.length === 0) return null;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Jost:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .ck-page { background:#FAF8F4; min-height:100vh; padding-top:130px; font-family:'Jost',sans-serif; }

        .ck-progress {
          max-width:520px; margin:0 auto 36px;
          padding:0 clamp(16px,4vw,48px);
          display:flex; align-items:center;
        }
        .ck-step { display:flex; align-items:center; gap:9px; }
        .ck-step-num {
          width:26px; height:26px; border-radius:50%;
          border:1.5px solid #D3CFC9; display:flex; align-items:center;
          justify-content:center; font-size:10px; font-weight:600;
          color:#B0A99F; background:#fff; flex-shrink:0; transition:all 0.3s;
        }
        .ck-step-num.active { background:#1C1A18; border-color:#1C1A18; color:#FAF8F4; }
        .ck-step-num.done   { background:#B08D57; border-color:#B08D57; color:#fff; }
        .ck-step-label {
          font-size:10px; font-weight:600; letter-spacing:0.16em;
          text-transform:uppercase; color:#B0A99F;
        }
        .ck-step-label.active { color:#1C1A18; }
        .ck-step-line { flex:1; height:1px; background:#E8E3DC; margin:0 14px; }
        .ck-step-line.done { background:#B08D57; }

        .ck-wrap {
          max-width:1020px; margin:0 auto;
          padding:0 clamp(16px,4vw,48px) 80px;
          display:grid; grid-template-columns:1fr 340px;
          gap:24px; align-items:start;
        }

        .ck-panel { background:#fff; border:1px solid rgba(176,141,87,0.14); margin-bottom:16px; }
        .ck-panel-head {
          padding:15px 22px; border-bottom:1px solid rgba(176,141,87,0.1);
          display:flex; align-items:center; justify-content:space-between;
        }
        .ck-panel-title { font-family:'Playfair Display',serif; font-size:16px; font-weight:700; color:#1C1A18; }
        .ck-panel-title em { font-style:italic; font-weight:400; color:#B08D57; }
        .ck-panel-body { padding:20px 22px; }

        .ck-item { display:flex; gap:14px; align-items:center; padding:11px 0; border-bottom:1px solid rgba(176,141,87,0.07); }
        .ck-item:last-of-type { border-bottom:none; }
        .ck-item-img { width:54px; height:54px; object-fit:cover; border:1px solid rgba(176,141,87,0.14); background:#F0EAE0; flex-shrink:0; }
        .ck-item-name { font-size:13px; font-weight:500; color:#1C1A18; margin-bottom:3px; }
        .ck-item-qty  { font-size:11px; color:#9A9186; }
        .ck-item-price { font-family:'Playfair Display',serif; font-size:14px; font-weight:700; color:#B08D57; white-space:nowrap; margin-left:auto; }

        .ck-row { display:flex; justify-content:space-between; font-size:12px; color:#5C5247; padding:6px 0; border-bottom:1px solid rgba(176,141,87,0.06); }
        .ck-row:last-child { border-bottom:none; }
        .ck-row.green { color:#2E7D32; }
        .ck-row.total { border-top:1px solid rgba(176,141,87,0.18); padding-top:12px; margin-top:8px; font-size:13px; font-weight:600; color:#1C1A18; }
        .ck-row.total span:last-child { font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:#B08D57; }

        .ck-voucher-banner {
          margin:12px 0; padding:9px 13px;
          display:flex; align-items:center; gap:8px;
          font-size:12px; font-weight:500;
        }
        .ck-voucher-banner.gift     { background:rgba(176,141,87,0.08); border:1px solid rgba(176,141,87,0.3);  color:#9A7A40; }
        .ck-voucher-banner.shipping { background:rgba(46,125,50,0.06);  border:1px solid rgba(46,125,50,0.2);   color:#2E7D32; }
        .ck-voucher-banner.nudge    { background:rgba(28,26,24,0.03);   border:1px solid rgba(28,26,24,0.08);   color:#8A7E72; }

        .ck-delivery-opt {
          border:1.5px solid rgba(176,141,87,0.16); padding:13px 16px;
          margin-bottom:10px; cursor:pointer;
          transition:border-color 0.2s, background 0.2s;
          display:flex; gap:12px; align-items:center;
        }
        .ck-delivery-opt:hover { border-color:rgba(176,141,87,0.38); }
        .ck-delivery-opt.sel   { border-color:#B08D57; background:rgba(176,141,87,0.03); }
        .ck-delivery-dot {
          width:17px; height:17px; border-radius:50%;
          border:1.5px solid #D3CFC9; flex-shrink:0;
          display:flex; align-items:center; justify-content:center;
          transition:border-color 0.2s;
        }
        .ck-delivery-opt.sel .ck-delivery-dot { border-color:#B08D57; }
        .ck-delivery-dot-fill { width:7px; height:7px; border-radius:50%; background:#B08D57; opacity:0; transition:opacity 0.2s; }
        .ck-delivery-opt.sel .ck-delivery-dot-fill { opacity:1; }
        .ck-delivery-name { font-size:13px; font-weight:600; color:#1C1A18; margin-bottom:3px; }
        .ck-delivery-days { font-size:11px; color:#8A7E72; }
        .ck-delivery-tag {
          display:inline-block; font-size:8px; font-weight:600;
          letter-spacing:0.12em; text-transform:uppercase;
          padding:2px 7px; margin-left:8px;
        }
        .ck-delivery-tag.recommended { background:rgba(176,141,87,0.1); color:#9A7A40; }
        .ck-delivery-tag.fastest     { background:rgba(28,26,24,0.07);  color:#5C5247; }
        .ck-delivery-charge {
          font-family:'Playfair Display',serif; font-size:14px;
          font-weight:700; white-space:nowrap; margin-left:auto;
        }

        .ck-addr {
          border:1.5px solid rgba(176,141,87,0.16); padding:13px 16px;
          margin-bottom:10px; cursor:pointer; transition:border-color 0.2s,background 0.2s;
          display:flex; gap:12px; align-items:flex-start;
        }
        .ck-addr:hover { border-color:rgba(176,141,87,0.38); }
        .ck-addr.sel { border-color:#B08D57; background:rgba(176,141,87,0.03); }
        .ck-addr-dot {
          width:17px; height:17px; border-radius:50%;
          border:1.5px solid #D3CFC9; flex-shrink:0; margin-top:2px;
          display:flex; align-items:center; justify-content:center; transition:border-color 0.2s;
        }
        .ck-addr.sel .ck-addr-dot { border-color:#B08D57; }
        .ck-addr-dot-fill { width:7px; height:7px; border-radius:50%; background:#B08D57; opacity:0; transition:opacity 0.2s; }
        .ck-addr.sel .ck-addr-dot-fill { opacity:1; }
        .ck-addr-name { font-size:13px; font-weight:600; color:#1C1A18; margin-bottom:4px; }
        .ck-addr-tag {
          display:inline-block; font-size:8px; font-weight:600;
          letter-spacing:0.12em; text-transform:uppercase;
          background:rgba(176,141,87,0.1); color:#9A7A40; padding:2px 7px; margin-left:8px;
        }
        .ck-addr-line { font-size:12px; color:#8A7E72; line-height:1.6; }

        .ck-no-addr { padding:24px; text-align:center; border:1px dashed rgba(176,141,87,0.22); font-size:13px; color:#8A7E72; }
        .ck-no-addr a { color:#B08D57; text-decoration:none; font-weight:500; }

        .ck-btn {
          width:100%; background:#1C1A18; color:#FAF8F4; border:none; padding:15px;
          font-family:'Jost',sans-serif; font-size:10px; font-weight:600;
          letter-spacing:0.22em; text-transform:uppercase; cursor:pointer;
          transition:background 0.25s; margin-top:16px;
          display:flex; align-items:center; justify-content:center; gap:8px;
        }
        .ck-btn:hover:not(:disabled) { background:#B08D57; }
        .ck-btn:disabled { opacity:0.5; cursor:not-allowed; }

        .ck-spinner { display:inline-block; width:11px; height:11px; border:2px solid rgba(255,255,255,0.3); border-top-color:#fff; border-radius:50%; animation:ck-spin 0.7s linear infinite; }
        .ck-spinner-row { display:flex; align-items:center; justify-content:center; gap:8px; }
        @keyframes ck-spin { to { transform:rotate(360deg); } }

        .ck-err { font-size:12px; color:#C0392B; margin-top:10px; padding:9px 13px; background:rgba(192,57,43,0.05); border:1px solid rgba(192,57,43,0.15); }

        .ck-pay-info {
          margin-top:16px; padding:16px; background:rgba(176,141,87,0.04);
          border:1px solid rgba(176,141,87,0.16); text-align:center;
        }
        .ck-pay-info p { font-size:12px; color:#8A7E72; line-height:1.7; }
        .ck-pay-info strong { color:#5C5247; font-weight:600; }
        .ck-pay-methods { display:flex; justify-content:center; gap:8px; margin-top:10px; flex-wrap:wrap; }
        .ck-pay-method-tag {
          font-size:9px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase;
          padding:4px 10px; border:1px solid rgba(176,141,87,0.22); color:#9A7A40;
          background:rgba(176,141,87,0.06);
        }
        .ck-secure-note { text-align:center; font-size:11px; color:#B0A99F; margin-top:12px; letter-spacing:0.03em; }

        .ck-summary { position:sticky; top:150px; }
        .ck-summary-box { background:#fff; border:1px solid rgba(176,141,87,0.14); overflow:hidden; }
        .ck-summary-head { background:#1C1A18; padding:14px 20px; }
        .ck-summary-head h3 { font-family:'Playfair Display',serif; font-size:15px; font-weight:700; color:#FAF8F4; }
        .ck-summary-head h3 em { font-style:italic; font-weight:400; color:#D4AA70; }
        .ck-summary-body { padding:16px 20px; }
        .ck-sum-item { display:flex; justify-content:space-between; font-size:12px; color:#5C5247; padding:5px 0; }
        .ck-sum-item span:first-child { flex:1; margin-right:8px; }
        .ck-sum-divider { height:1px; background:rgba(176,141,87,0.1); margin:8px 0; }
        .ck-sum-total { display:flex; justify-content:space-between; align-items:baseline; padding-top:10px; margin-top:4px; border-top:1px solid rgba(176,141,87,0.15); font-size:12px; font-weight:600; color:#1C1A18; }
        .ck-sum-total span:last-child { font-family:'Playfair Display',serif; font-size:19px; font-weight:700; color:#B08D57; }
        .ck-sum-green { color:#2E7D32; }

        .ck-trust { display:flex; border-top:1px solid rgba(176,141,87,0.1); margin-top:16px; padding-top:14px; gap:4px; }
        .ck-trust-item { flex:1; text-align:center; font-size:8px; font-weight:600; letter-spacing:0.12em; text-transform:uppercase; color:#9A9186; line-height:1.5; }
        .ck-trust-line { width:1px; background:rgba(176,141,87,0.12); }

        @media (max-width:760px) {
          .ck-wrap { grid-template-columns:1fr; }
          .ck-summary { position:static; }
          .ck-step-label { display:none; }
        }
      `}</style>

      <div className="ck-page">

        {/* Progress */}
        <div className="ck-progress">
          <div className="ck-step">
            <div className="ck-step-num active">1</div>
            <span className="ck-step-label active">Order</span>
          </div>
          <div className="ck-step-line" />
          <div className="ck-step">
            <div className={`ck-step-num ${addressId ? 'active' : ''}`}>2</div>
            <span className={`ck-step-label ${addressId ? 'active' : ''}`}>Address</span>
          </div>
          <div className="ck-step-line" />
          <div className="ck-step">
            <div className="ck-step-num">3</div>
            <span className="ck-step-label">Payment</span>
          </div>
        </div>

        <div className="ck-wrap">
          <div>

            {/* ── Order Items ── */}
            <div className="ck-panel">
              <div className="ck-panel-head">
                <h2 className="ck-panel-title">Your <em>Order</em></h2>
                <span style={{ fontSize: 11, color: '#9A9186' }}>{items.length} item{items.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="ck-panel-body">
                {items.map((item, i) => (
                  <div key={i} className="ck-item">
                    {item.image
                      ? <img src={item.image} alt={item.name} className="ck-item-img" />
                      : <div className="ck-item-img" />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="ck-item-name">{item.name}</p>
                      <p className="ck-item-qty">Qty: {item.qty}</p>
                    </div>
                    <span className="ck-item-price">{formatPrice(item.price * item.qty)}</span>
                  </div>
                ))}

                {/* Totals */}
                <div style={{ marginTop: 16 }}>
                  <div className="ck-row"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                  {voucherDiscount > 0 && (
                    <div className="ck-row green"><span>Voucher Discount</span><span>−{formatPrice(voucherDiscount)}</span></div>
                  )}
                  <div className="ck-row">
                  <span>Shipping</span>
                  <span style={{ color: shippingCharge === 0 ? '#2E7D32' : '#8A7E72' }}>
                    {isIntl ? 'Quoted on WhatsApp' : shippingCharge === 0 ? 'Free' : formatPrice(shippingCharge)}
                  </span>
                </div>
                  <div className="ck-row total">
                  <span>Total</span>
                  <span>{isIntl ? `${formatPrice(subtotal)} + shipping` : formatPrice(total)}</span>
                </div>
                </div>

                {/* Voucher Banner */}
               {!isIntl && voucher.label && (
                  <div className={`ck-voucher-banner ${voucher.freeGift ? 'gift' : subtotal >= 1499 ? 'shipping' : 'nudge'}`}>
                    {voucher.label}
                  </div>
                )}
              </div>
            </div>

            {/* ── Delivery Options — domestic only ── */}
            {!isIntl && (
              <div className="ck-panel">
                <div className="ck-panel-head">
                  <h2 className="ck-panel-title">Delivery <em>Options</em></h2>
                  {addressId && (
                    <span style={{ fontSize: 11, color: '#9A9186' }}>
                      {zone === 'kerala' ? 'Kerala' : zone === 'south' ? 'South India' : 'North India'}
                    </span>
                  )}
                </div>
                <div className="ck-panel-body">
                  {!addressId ? (
                    <p style={{ fontSize: 13, color: '#8A7E72' }}>Select an address first to see delivery options</p>
                  ) : deliveryOptions.map(opt => (
                    <div
                      key={opt.id}
                      className={`ck-delivery-opt${selectedDelivery?.id === opt.id ? ' sel' : ''}`}
                      onClick={() => setSelectedDelivery(opt)}
                    >
                      <div className="ck-delivery-dot"><div className="ck-delivery-dot-fill" /></div>
                      <div style={{ flex: 1 }}>
                        <p className="ck-delivery-name">
                          {opt.partner} — {opt.type}
                          {opt.badge && (
                            <span className={`ck-delivery-tag ${opt.badge === 'Recommended' ? 'recommended' : 'fastest'}`}>
                              {opt.badge}
                            </span>
                          )}
                        </p>
                        <p className="ck-delivery-days">{opt.days}</p>
                      </div>
                      <span className="ck-delivery-charge" style={{ color: opt.charge === 0 ? '#2E7D32' : '#B08D57' }}>
                        {opt.charge === 0 ? 'Free' : formatPrice(opt.charge)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Address ── */}
            <div className="ck-panel">
              <div className="ck-panel-head">
                <h2 className="ck-panel-title">Deliver <em>to</em></h2>
              </div>
              <div className="ck-panel-body">
                {addresses.length === 0 ? (
                  <div className="ck-no-addr">
                    <p style={{ marginBottom: 10 }}>No saved addresses found.</p>
                    <a href="/account">Add a delivery address</a>
                  </div>
                ) : (
                  addresses.map(a => (
                    <div key={a.id} className={`ck-addr${addressId === a.id ? ' sel' : ''}`} onClick={() => setAddressId(a.id)}>
                      <div className="ck-addr-dot"><div className="ck-addr-dot-fill" /></div>
                      <div>
                        <p className="ck-addr-name">
                          {a.full_name}
                          <span className="ck-addr-tag">{a.label}</span>
                          {a.is_default && <span className="ck-addr-tag" style={{ background: 'rgba(28,26,24,0.07)', color: '#5C5247' }}>Default</span>}
                        </p>
                        <p className="ck-addr-line">{a.line_1}{a.line_2 ? ', ' + a.line_2 : ''}</p>
                        <p className="ck-addr-line">{a.city}, {a.state} — {a.pincode}</p>
                        <p className="ck-addr-line">{a.country || 'India'}</p>
                        <p className="ck-addr-line">{a.phone}</p>
                      </div>
                    </div>
                  ))
                )}

                

{err && <div className="ck-err">{err}</div>}

                {isIntl && addressId && (
                  <>
                    <div style={{
                      marginTop: 16,
                      marginBottom: 12,
                      padding: '16px 18px',
                      background: 'rgba(176,141,87,0.05)',
                      border: '1px solid rgba(176,141,87,0.2)',
                    }}>
                      <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 14, fontWeight: 700, color: '#1C1A18', marginBottom: 8 }}>
                        Why WhatsApp for international orders?
                      </p>
                      <ul style={{ fontSize: 12, color: '#5C5247', lineHeight: 1.9, paddingLeft: 16, margin: 0 }}>
                        <li>Shipping costs vary by your country, region, and parcel weight</li>
                        <li>Our team confirms the exact shipping quote before you pay</li>
                        <li>We assist with customs documentation if needed</li>
                        <li>Payment is arranged securely after quote confirmation</li>
                      </ul>
                    </div>
                    
                      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage(items, addresses.find(a => a.id === addressId))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <button className="ck-btn" style={{ background: '#25D366', marginTop: 0 }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white" style={{ flexShrink: 0 }}>
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                          <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.535 5.858L.057 23.428a.75.75 0 00.916.916l5.57-1.478A11.952 11.952 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75a9.718 9.718 0 01-4.964-1.362l-.356-.212-3.696.98.981-3.595-.232-.371A9.718 9.718 0 012.25 12C2.25 6.615 6.615 2.25 12 2.25S21.75 6.615 21.75 12 17.385 21.75 12 21.75z"/>
                        </svg>
                        Chat with us on WhatsApp
                      </button>
                    </a>
                    <p className="ck-secure-note" style={{ marginTop: 10 }}>Our team typically responds within a few hours</p>
                  </>
                )}

                {!isIntl && (
                  <>
                    <div className="ck-pay-info">
                      <p>You will be redirected to <strong>Razorpay</strong> secure payment page after clicking the button below.</p>
                      <div className="ck-pay-methods">
                        <span className="ck-pay-method-tag">UPI</span>
                        <span className="ck-pay-method-tag">Cards</span>
                        <span className="ck-pay-method-tag">Net Banking</span>
                        <span className="ck-pay-method-tag">Wallets</span>
                      </div>
                    </div>
                    <button
                      className="ck-btn"
                      onClick={handlePayment}
                      disabled={loading || !addressId || !selectedDelivery}
                    >
                      {loading
                        ? <span className="ck-spinner-row"><span className="ck-spinner" /> Opening Payment…</span>
                        : `Pay ${formatPrice(total)}`}
                    </button>
                    <p className="ck-secure-note">Your payment is encrypted and secured by Razorpay</p>
                  </>
                )}

              </div>
            </div>

          </div>

          {/* ── Right Summary ── */}
          <div className="ck-summary">
            <div className="ck-summary-box">
              <div className="ck-summary-head">
                <h3>Order <em>Summary</em></h3>
              </div>
              <div className="ck-summary-body">
                {items.map((item, i) => (
                  <div key={i} className="ck-sum-item">
                    <span>{item.name} × {item.qty}</span>
                    <span>{formatPrice(item.price * item.qty)}</span>
                  </div>
                ))}
                <div className="ck-sum-divider" />
                <div className="ck-sum-item"><span>Subtotal</span><span>{formatPrice(subtotal)}</span></div>
                {voucherDiscount > 0 && (
                  <div className="ck-sum-item ck-sum-green"><span>Voucher</span><span>−{formatPrice(voucherDiscount)}</span></div>
                )}
                {voucher.freeGift && (
                  <div className="ck-sum-item ck-sum-green"><span>Free Gift</span><span>✓</span></div>
                )}
               <div className="ck-sum-item">
              <span>Shipping {!isIntl && selectedDelivery ? `(${selectedDelivery.partner})` : ''}</span>
              <span style={{ color: shippingCharge === 0 ? '#2E7D32' : '#8A7E72' }}>
                {isIntl ? 'Via WhatsApp' : shippingCharge === 0 ? 'Free' : formatPrice(shippingCharge)}
              </span>
            </div>
               <div className="ck-sum-total">
              <span>Total</span>
              <span>{isIntl ? `${formatPrice(subtotal)}+` : formatPrice(total)}</span>
            </div>
                <div className="ck-trust">
                  <div className="ck-trust-item">Secure<br />Payment</div>
                  <div className="ck-trust-line" />
                  <div className="ck-trust-item">Fast<br />Delivery</div>
                  <div className="ck-trust-line" />
                  <div className="ck-trust-item">Easy<br />Returns</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Checkout;